import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, siteStats } from '@/mocks/siteInfo';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、平台口碑与联系方式。`,
    url: 'https://jmcomic-guide.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://jmcomic-guide.com/',
    description: siteInfo.description,
    foundingDate: siteInfo.established,
    contactPoint: {
      '@type': 'ContactPoint',
      email: siteInfo.email,
      contactType: 'customer service',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmcomic-guide.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const timelineEvents = [
    { year: '2018', title: '平台创立', description: '禁漫天堂 JMComic 正式上线，以简洁的界面和流畅的阅读体验迅速吸引首批用户。' },
    { year: '2019', title: '用户破百万', description: '凭借丰富的漫画资源和稳定服务，注册用户数突破100万，成为漫画阅读领域的新锐力量。' },
    { year: '2020', title: '移动端优化', description: '全面优化移动端阅读体验，支持响应式布局和离线下载，移动端用户占比超过70%。' },
    { year: '2022', title: '镜像站体系', description: '建立完善的镜像站点体系，确保用户在任何网络环境下都能稳定访问，用户体验大幅提升。' },
    { year: '2024', title: '五百万用户', description: '平台注册用户数突破500万，漫画资源总量超过10万部，成为国内领先的漫画阅读平台之一。' },
    { year: '2026', title: '持续精进', description: '不断优化产品体验，引入AI智能推荐，为漫画爱好者提供更加个性化的阅读服务。' },
  ];

  const testimonials = [
    { name: '漫画爱好者小王', text: '用了禁漫天堂三年多了，资源丰富更新快，阅读体验一直很好。离线下载功能特别实用，通勤路上也能看。', rating: 5 },
    { name: '日漫粉丝小林', text: '分类很全很细致，找漫画特别方便。最喜欢它的收藏和更新提醒功能，追更再也不会错过了。', rating: 5 },
    { name: '资深读者老张', text: '从2019年就开始用了，看着平台一步步成长。现在的阅读器流畅度和画质都非常满意，推荐给很多朋友了。', rating: 5 },
    { name: '韩漫爱好者阿美', text: '韩漫资源很全，翻译质量也很高。支持多端同步太方便了，手机电脑无缝切换，阅读进度自动保存。', rating: 4 },
  ];

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteInfo.name} 品牌介绍与联系方式`}
        description={`了解${siteInfo.name}的品牌故事、发展历程、用户口碑和联系方式。${siteInfo.description.slice(0, 60)}`}
        keywords="禁漫天堂,关于禁漫天堂,禁漫天堂品牌,禁漫天堂联系方式"
        canonicalUrl="https://jmcomic-guide.com/about"
      />
      <SchemaOrg schema={aboutSchema} />
      <SchemaOrg schema={orgSchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <main>
          <section className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  <i className="ri-building-line text-sm"></i>
                  关于我们
                </div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  关于禁漫天堂 JMComic
                </h1>
                <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                  {siteInfo.description}
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-14">
                {siteStats.map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="text-xl md:text-2xl font-heading font-bold text-foreground-900">{stat.value}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-18 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-12">
                发展历程
              </h2>
              <div className="relative">
                <div className="hidden sm:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-background-300/60 -translate-x-1/2"></div>
                <div className="space-y-8">
                  {timelineEvents.map((event, index) => (
                    <div key={event.year} className={`flex flex-col sm:flex-row items-start gap-4 ${index % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}>
                      <div className={`flex-1 ${index % 2 === 0 ? 'sm:text-right' : 'sm:text-left'} w-full`}>
                        <div className={`p-5 rounded-xl bg-background-50 border border-background-200/60 ${index % 2 === 0 ? 'sm:mr-6' : 'sm:ml-6'}`}>
                          <div className="text-sm font-heading font-bold text-primary-600 mb-1">{event.year}</div>
                          <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">{event.title}</h3>
                          <p className="text-sm text-foreground-600">{event.description}</p>
                        </div>
                      </div>
                      <div className="hidden sm:flex w-10 h-10 items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0 relative z-10">
                        <i className="ri-check-line"></i>
                      </div>
                      <div className="flex-1 hidden sm:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-18 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-center text-sm mb-10 max-w-lg mx-auto">
                来自禁漫天堂 JMComic 真实用户的评价
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {testimonials.map((t, i) => (
                  <div key={i} className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, starIdx) => (
                        <i
                          key={starIdx}
                          className={`text-sm ${starIdx < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                      &ldquo;{t.text}&rdquo;
                    </p>
                    <div className="text-xs font-semibold text-foreground-900">{t.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-18 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">电子邮箱</h3>
                  <a href={`mailto:${siteInfo.email}`} className="text-sm text-primary-600 hover:text-primary-700 break-all whitespace-nowrap cursor-pointer">
                    {siteInfo.email}
                  </a>
                </div>
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-global-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">官方网站</h3>
                  <a href={`https://${siteInfo.latestAddress}`} target="_blank" rel="nofollow noopener noreferrer" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer">
                    {siteInfo.latestAddress}
                  </a>
                </div>
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-question-answer-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-900 mb-2">反馈建议</h3>
                  <p className="text-sm text-foreground-500">
                    通过邮件发送您的<br />意见与建议
                  </p>
                </div>
              </div>

              <div className="mt-8 p-5 rounded-xl bg-secondary-50 border border-secondary-200/50 text-center">
                <p className="text-sm text-secondary-700">
                  <strong>声明：</strong>本站为第三方使用指南网站，旨在帮助用户了解和使用禁漫天堂 JMComic 平台。
                  我们与禁漫天堂官方无直接关联，所有品牌和商标归其各自所有者所有。
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}